import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  role: null,
  permissions: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
      role: action.payload.role ?? null,
      permissions: action.payload.permissions ?? null,
    };
  }
  if (action.type === 'LOGIN' ||
      action.type === 'ACCOUNT' ||
      action.type === 'REGISTER'
    ) {
    return {
      ...state,
      user: action.payload.user,
      role: action.payload.role ?? state.role,
      permissions: action.payload.permissions ?? state.permissions,
    };
  }

  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
      role: null,
      permissions: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get(API_ENDPOINTS.auth.me);

        const { admin } = response.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            user: admin.user,
            role: admin.role,
            permissions: admin.permissions,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
            role: null,
            permissions: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
          role: null,
          permissions: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const loginData = {
      email,
      password,
    };

    const response = await axios.post(API_ENDPOINTS.auth.login, loginData);
    
    const { token, admin } = response.data.data;
    
    setSession(token);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: admin.user,
        role: admin.role,
        permissions: admin.permissions,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstname, lastname, password2) => {
    const data = {
      email,
      password,
      firstname,
      lastname,
      password2
    };

    const response = await axios.post(API_ENDPOINTS.auth.register, data);

    const { accessToken, user } = response.data;

    localStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);
  //UPDATE
  const update = useCallback(async(data)=>{
    const response = await axios.post(API_ENDPOINTS.auth.update, data);
    const { user } = response.data;
    dispatch({
      type: 'ACCOUNT',
      payload: {
        user,
      },
    });
  })
  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // ----------------------------------------------------------------------

  const hasPermission = useCallback((permissionKey) => {
    // Null/undefined permissionKey means always visible
    if (!permissionKey) return true;
    // No permissions loaded yet
    if (!state.permissions) return false;
    // Walk dot-notation paths (e.g. 'campaigns.email') so nested permission
    // objects from the API can be checked at any depth.
    const parts = String(permissionKey).split('.');
    let node = state.permissions;
    for (const part of parts) {
      if (node == null || typeof node !== 'object') return false;
      node = node[part];
    }
    return !!node;
  }, [state.permissions]);

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      role: state.role,
      permissions: state.permissions,
      hasPermission,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      update,
      logout,
    }),
    [login, logout, register, state.user, state.role, state.permissions, hasPermission, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
